import React, { useState } from 'react'
import { Toaster } from 'react-hot-toast'
import {FileText, BookOpen, LogOut, LayoutDashboard, ArrowLeft} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { lumi } from '@/lib/lumi'
import AdminLogin from '@/components/admin/AdminLogin'
import PublicationsManager from '@/components/admin/PublicationsManager'
import BooksManager from '@/components/admin/BooksManager'
import Logo from '@/components/Logo'

type Tab = 'publications' | 'books'

const TABS = [
  { id: 'publications' as Tab, label: 'Publications', icon: FileText },
  { id: 'books' as Tab, label: 'Livres en vente', icon: BookOpen },
]

const Admin: React.FC = () => {
  const { isAuthenticated, isAdmin, user } = useAuth()
  const [activeTab, setActiveTab] = useState<Tab>('publications')

  const handleSignOut = async () => {
    await lumi.auth.signOut()
  }

  // Non connecté → écran de connexion
  if (!isAuthenticated) {
    return (
      <>
        <AdminLogin />
        <Toaster position="bottom-right" toastOptions={{ style: { background: '#14253B', color: '#E8DCC4', border: '1px solid rgba(232,220,196,0.15)', fontSize: '13px' } }} />
      </>
    )
  }

  // Connecté mais pas admin → accès refusé
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#0E1B2C] flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <p className="text-[#C9A86A] tracking-[0.3em] text-xs uppercase mb-4">Accès restreint</p>
          <h1 className="font-serif text-3xl text-[#E8DCC4] mb-4">Droits insuffisants</h1>
          <p className="text-[#E8DCC4]/50 text-sm mb-8">
            Votre compte ne dispose pas des droits d'administration nécessaires.
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={handleSignOut}
              className="px-6 py-3 border border-[#E8DCC4]/15 text-[#E8DCC4]/70 rounded-xl hover:border-[#E8DCC4]/40 transition-colors text-sm"
            >
              Se déconnecter
            </button>
            <a href="/" className="text-[#A3C644] text-sm hover:underline">
              ← Retour au site
            </a>
          </div>
        </div>
        <Toaster position="bottom-right" toastOptions={{ style: { background: '#14253B', color: '#E8DCC4', border: '1px solid rgba(232,220,196,0.15)', fontSize: '13px' } }} />
      </div>
    )
  }

  // Admin connecté → dashboard
  return (
    <div className="min-h-screen bg-[#0E1B2C] flex">
      {/* Sidebar */}
      <aside className="w-64 flex-shrink-0 bg-[#0A1525] border-r border-[#E8DCC4]/10 flex flex-col">
        {/* Logo */}
        <div className="px-6 py-6 border-b border-[#E8DCC4]/10">
          <Logo size={36} variant="lockup" />
          <p className="text-[#C9A86A] text-xs tracking-[0.2em] uppercase mt-3">Administration</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-6 space-y-1">
          <p className="text-[#E8DCC4]/30 text-xs uppercase tracking-widest px-3 mb-3">Contenu</p>
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left ${
                activeTab === id
                  ? 'bg-[#A3C644]/15 text-[#A3C644] border border-[#A3C644]/20'
                  : 'text-[#E8DCC4]/60 hover:text-[#E8DCC4] hover:bg-[#E8DCC4]/5'
              }`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {label}
            </button>
          ))}
        </nav>

        {/* Footer sidebar */}
        <div className="px-3 py-4 border-t border-[#E8DCC4]/10 space-y-1">
          <a
            href="/"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#E8DCC4]/50 hover:text-[#E8DCC4] hover:bg-[#E8DCC4]/5 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voir le site
          </a>
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#E8DCC4]/50 hover:text-red-400 hover:bg-red-400/5 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Se déconnecter
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-h-screen overflow-auto">
        {/* Top bar */}
        <header className="px-8 py-5 border-b border-[#E8DCC4]/10 flex items-center justify-between bg-[#0E1B2C] sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <LayoutDashboard className="w-5 h-5 text-[#C9A86A]" />
            <h1 className="text-[#E8DCC4] font-medium">
              {TABS.find((t) => t.id === activeTab)?.label}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[#E8DCC4]/40 text-sm">{user?.email}</span>
            <span className="text-xs px-2.5 py-1 rounded-full bg-[#A3C644]/20 text-[#A3C644] border border-[#A3C644]/30">
              Admin
            </span>
          </div>
        </header>

        {/* Tab content */}
        <div className="flex-1 px-8 py-8">
          {activeTab === 'publications' && <PublicationsManager />}
          {activeTab === 'books' && <BooksManager />}
        </div>
      </main>

      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#14253B',
            color: '#E8DCC4',
            border: '1px solid rgba(232,220,196,0.15)',
            fontSize: '13px',
            letterSpacing: '0.04em',
          },
        }}
      />
    </div>
  )
}

export default Admin
